import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Output,
    inject,
} from '@angular/core';
import { orderBy as _orderBy } from 'lodash';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxScrollViewComponent } from 'devextreme-angular/ui/scroll-view';
import { DxSelectBoxComponent } from 'devextreme-angular/ui/select-box';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import {
    MessageType,
    NotificationService,
    UtilService,
} from 'src/app/shared/services';
import { FormatNumberPipe } from '../../../shared/pipes/format-number.pipe';
import {
    Supplier1688Index,
    Supplier1688Row,
    SupplierOverridePart,
    SupplierOverridesDoc,
    SupplierProductChange,
    SupplierProductOverride,
    lastPaidForOption,
    optionLabel,
    parseOfferLink,
    parseShopLink,
} from '../supplier-1688';
import { Supplier1688Service } from '../supplier-1688.service';

interface Supplier1688EditorTarget {
    pid: number;
    sku: string;
    title: string;
}

interface DraftPart {
    link: string;
    label: string;
    partName: string;
    packQty: number;
    unitPriceCny: number | null;
}

// Sets what a person knows about one product's 1688 purchase. Everything saved
// here lands in the overrides document, away from the rows the sync replaces.
@Component({
    selector: 'app-supplier-1688-editor',
    templateUrl: './supplier-1688-editor.component.html',
    styleUrls: ['./supplier-1688-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxButtonComponent,
        DxNumberBoxComponent,
        DxPopupComponent,
        DxScrollViewComponent,
        DxSelectBoxComponent,
        DxTextBoxComponent,
        FormatNumberPipe,
    ],
})
export class Supplier1688EditorComponent {
    private service = inject(Supplier1688Service);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);

    @Output() saved = new EventEmitter<SupplierOverridesDoc>();

    visible = false;
    saving = false;
    target: Supplier1688EditorTarget | null = null;
    latestRows: Supplier1688Row[] = [];
    earlierRows: Supplier1688Row[] = [];
    override: SupplierProductOverride | null = null;
    draftParts: DraftPart[] = [];
    shopItems: { value: string; text: string }[] = [];
    shopUrl: string | null = null;
    addingShop = false;
    newShopLink = '';
    newShopName = '';
    note = '';
    // Fields only turn red once saving has been tried.
    submitted = false;
    private allRows: Supplier1688Row[] = [];
    private productRows: Supplier1688Row[] = [];

    open(target: Supplier1688EditorTarget, index: Supplier1688Index): void {
        this.target = target;
        this.allRows = index.rows;
        this.productRows = index.rows.filter((row) => row.pid === target.pid);
        this.latestRows = this.productRows.filter((row) => row.isLatest);
        this.earlierRows = _orderBy(
            this.productRows.filter((row) => !row.isLatest),
            'lastOrderDate',
            'desc',
        );
        this.override = index.overrides.products[target.pid] ?? null;
        this.shopItems = index.shops.map((shop) => ({
            value: shop.url,
            text: shop.name,
        }));
        const userParts = this.override?.parts;
        this.draftParts = userParts?.length
            ? userParts.map((part) => this.draftFromOverride(part))
            : this.latestRows.map((row) => this.draftFromRow(row));
        if (this.draftParts.length === 0) {
            this.draftParts = [this.emptyDraft()];
        }
        this.shopUrl =
            this.override?.shopUrl ?? this.latestRows[0]?.sellerUrl ?? null;
        this.note = this.override?.note ?? '';
        this.addingShop = false;
        this.newShopLink = '';
        this.newShopName = '';
        this.submitted = false;
        this.visible = true;
    }

    private emptyDraft(): DraftPart {
        return {
            link: '',
            label: '',
            partName: '',
            packQty: 1,
            unitPriceCny: null,
        };
    }

    private draftFromRow(row: Supplier1688Row): DraftPart {
        return {
            link: row.url,
            label: optionLabel(row),
            partName: row.part ?? '',
            packQty: row.packQty,
            // Blank keeps following this option's last order.
            unitPriceCny: null,
        };
    }

    private draftFromOverride(part: SupplierOverridePart): DraftPart {
        return {
            link: part.url,
            label: part.label,
            partName: part.partName ?? '',
            packQty: part.packQty,
            unitPriceCny: part.unitPriceCny,
        };
    }

    close(): void {
        this.visible = false;
    }

    addPart(): void {
        this.draftParts = [...this.draftParts, this.emptyDraft()];
    }

    removePart(part: DraftPart): void {
        this.draftParts = this.draftParts.filter((p) => p !== part);
    }

    // A pasted link from a shop we already know brings its shop along.
    onLinkChanged(part: DraftPart): void {
        const offer = parseOfferLink(part.link);
        if (!offer) {
            return;
        }
        const known = this.allRows.find((row) => row.offerId === offer.offerId);
        if (known && !this.addingShop) {
            this.shopUrl = known.sellerUrl;
        }
    }

    useEarlierRow(row: Supplier1688Row): void {
        this.draftParts = [this.draftFromRow(row)];
        this.shopUrl = row.sellerUrl;
        this.addingShop = false;
    }

    pricePlaceholder(part: DraftPart): string {
        const offer = parseOfferLink(part.link);
        const last =
            offer &&
            lastPaidForOption(this.productRows, offer.offerId, part.label);
        return last ? `ล่าสุด ${last.unitPriceCny}` : 'ยังไม่มีราคา';
    }

    isLinkValid(part: DraftPart): boolean {
        return !this.submitted || !!parseOfferLink(part.link);
    }

    isLabelValid(part: DraftPart): boolean {
        return !this.submitted || part.label.trim() !== '';
    }

    get isShopValid(): boolean {
        return !this.submitted || !!this.chosenShop();
    }

    get isNewShopNameValid(): boolean {
        return !this.submitted || this.newShopName.trim() !== '';
    }

    get canConfirm(): boolean {
        return this.latestRows.length > 0 && !this.override?.parts;
    }

    private chosenShop(): string | null {
        return this.addingShop ? parseShopLink(this.newShopLink) : this.shopUrl;
    }

    private isValid(): boolean {
        return (
            !!this.chosenShop() &&
            (!this.addingShop || this.newShopName.trim() !== '') &&
            this.draftParts.length > 0 &&
            this.draftParts.every(
                (part) =>
                    !!parseOfferLink(part.link) &&
                    part.label.trim() !== '' &&
                    part.packQty >= 1,
            )
        );
    }

    save(): void {
        this.submitted = true;
        if (!this.isValid()) {
            this.notificationService.pushMessage({
                displayTime: 4000,
                message:
                    'กรอกลิงก์ ตัวเลือก จำนวนต่อหน่วย และร้านให้ครบก่อนบันทึก',
                messageType: MessageType.error,
            });
            return;
        }
        const shopUrl = this.chosenShop();
        const parts: SupplierOverridePart[] = this.draftParts.map((part) => {
            const offer = parseOfferLink(part.link)!;
            return {
                url: offer.url,
                offerId: offer.offerId,
                label: part.label.trim(),
                partName: part.partName.trim() || null,
                packQty: Math.round(part.packQty),
                unitPriceCny: part.unitPriceCny,
            };
        });
        this.commit(
            { parts, shopUrl },
            'บันทึกค่าที่ตั้งเองแล้ว',
            this.addingShop && shopUrl
                ? { url: shopUrl, name: this.newShopName.trim() }
                : undefined,
        );
    }

    // The product is bought elsewhere, so whatever was matched is a mistake.
    markNotOn1688(): void {
        this.commit({ parts: [] }, 'บันทึกว่าไม่ได้ซื้อจาก 1688 แล้ว');
    }

    confirmMatch(): void {
        this.commit(
            { confirmedOffers: this.latestRows.map((row) => row.offerId) },
            'ยืนยันข้อมูลที่ match มาแล้ว',
        );
    }

    useMatched(): void {
        this.commit(null, 'กลับไปใช้ข้อมูลที่ match มาแล้ว');
    }

    // Not cancelled with the page: an edit already made must still reach the
    // store, however the user leaves.
    private commit(
        change: SupplierProductChange | null,
        message: string,
        newShop?: { url: string; name: string },
    ): void {
        if (this.saving || !this.target) {
            return;
        }
        this.saving = true;
        const withNote = change && {
            ...change,
            note: this.note.trim() || null,
        };
        this.service.saveProduct(this.target.pid, withNote, newShop).subscribe({
            next: (doc) => {
                this.saving = false;
                this.visible = false;
                this.saved.emit(doc);
                this.notificationService.pushMessage({
                    displayTime: 3000,
                    message,
                    messageType: MessageType.success,
                });
            },
            error: (err: unknown) => {
                this.saving = false;
                console.error(err);
                this.notificationService.pushMessage({
                    displayTime: 6000,
                    message: this.utilService.errorToString(err),
                    messageType: MessageType.error,
                });
            },
        });
    }
}
